import React, {useState, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useDatabase} from '../context/DatabaseContext';
import {useToast} from '../context/ToastContext';
import {colors} from '../theme/colors';

const WaterTrackerScreen = () => {
  const navigate = useNavigate();
  const {getWaterIntake, addWaterIntake, setWaterIntake, getWaterHistory} = useDatabase();
  const {success, error: showError} = useToast();
  const [todayIntake, setTodayIntake] = useState(0);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);

  const GOAL_ML = 2000; // 2 liters default goal

  useEffect(() => {
    loadTodayIntake();
  }, [selectedDate]);

  const loadTodayIntake = async () => {
    try {
      const dateString = selectedDate.toISOString().split('T')[0];
      const intake = await getWaterIntake(dateString);
      setTodayIntake(intake || 0);
    } catch (err) {
      console.error('Error loading water intake:', err);
      showError('נכשל בטעינת נתוני מים');
    }
  };


  
  const handleAddWater = async (amount) => {
    setLoading(true);
    try {
      const dateString = selectedDate.toISOString().split('T')[0];
      const added = await addWaterIntake(amount, dateString);
      if (added) {
        success(`נוספו ${amount} מ"ל`);
        await loadTodayIntake();
      } else {
        showError('נכשל בהוספת מים');
      }
    } catch (err) {
      console.error('Error adding water:', err);
      showError('נכשל בהוספת מים');
    } finally {
      setLoading(false);
    }
  };

  const handleSetWater = async (amount) => {
    setLoading(true);
    try {
      const dateString = selectedDate.toISOString().split('T')[0];
      const set = await setWaterIntake(amount, dateString);
      if (set) {
        success(`עודכן ל-${amount} מ"ל`);
        await loadTodayIntake();
      } else {
        showError('נכשל בעדכון מים');
      }
    } catch (err) {
      console.error('Error setting water:', err);
      showError('נכשל בעדכון מים');
    } finally {
      setLoading(false);
    }
  };

  const navigateToDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setSelectedDate(newDate);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('he-IL', {
      weekday: 'long',
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const isToday = () => {
    const today = new Date();
    return selectedDate.toISOString().split('T')[0] === today.toISOString().split('T')[0];
  };

  const progress = Math.min((todayIntake / GOAL_ML) * 100, 100);
  const remaining = Math.max(GOAL_ML - todayIntake, 0);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button
          style={styles.backButton}
          onClick={() => navigate('/')}
          aria-label="Go back to home"
          type="button">
          <span aria-hidden="true">←</span>
        </button>
        <div style={styles.headerTitle}>מעקב מים</div>
        <div style={styles.headerRight}></div>
      </div>

      {/* Date Navigation */}
      <div style={styles.dateSection}>
        <button
          style={styles.dateNavButton}
          onClick={() => navigateToDate('prev')}
          aria-label="Previous day"
          type="button">
          <span aria-hidden="true">←</span>
        </button>
        <div style={styles.dateText}>
          {formatDate(selectedDate)}
        </div>
        <button
          style={styles.dateNavButton}
          onClick={() => navigateToDate('next')}
          disabled={isToday()}
          aria-label="Next day"
          type="button">
          <span aria-hidden="true">→</span>
        </button>
      </div>

      {/* Water Intake Display */}
      <div style={styles.intakeCard}>
        <div style={styles.intakeValue}>{todayIntake.toFixed(0)}</div>
        <div style={styles.intakeUnit}>מ"ל</div>
        <div style={styles.intakeGoal}>מתוך {GOAL_ML.toLocaleString()} מ"ל</div>
      </div>

      {/* Progress Bar */}
      <div style={styles.progressSection}>
        <div style={styles.progressBarContainer}>
          <div
            style={{
              ...styles.progressBar,
              width: `${progress}%`,
            }}
          />
        </div>
        <div style={styles.progressText}>
          {progress.toFixed(0)}% מהיעד
        </div>
      </div>

      {/* Quick Add Buttons */}
      <div style={styles.quickAddSection}>
        <div style={styles.quickAddTitle}>הוסף מים</div>
        <div style={styles.quickAddButtons}>
          {[100, 200, 250, 500].map(amount => (
            <button
              key={amount}
              style={styles.quickAddButton}
              onClick={() => handleAddWater(amount)}
              disabled={loading}
              className="btn-secondary"
              aria-label={`Add ${amount} milliliters of water`}
              type="button">
              +{amount} מ"ל
            </button>
          ))}
        </div>
      </div>

      {/* Remaining */}
      {remaining > 0 && (
        <div style={styles.remainingCard}>
          <div style={styles.remainingIcon} aria-hidden="true">💧</div>
          <div style={styles.remainingText}>
            נותרו {remaining.toFixed(0)} מ"ל ליעד היומי
          </div>
        </div>
      )}

      {/* Goal Reached */}
      {todayIntake >= GOAL_ML && (
        <div style={styles.successCard}>
          <div style={styles.successIcon} aria-hidden="true">🎉</div>
          <div style={styles.successText}>השגת את היעד היומי!</div>
        </div>
      )}

      {/* Custom Amount Input */}
      <div style={styles.customSection}>
        <div style={styles.customTitle}>כמות מותאמת אישית</div>
        <div style={styles.customInputContainer}>
          <label htmlFor="customWaterInput" style={{display: 'none'}}>כמות מים במיליליטרים</label>
          <input
            type="number"
            placeholder='מ"ל'
            min="0"
            max="5000"
            style={styles.customInput}
            id="customWaterInput"
            dir="ltr"
            aria-label="Custom water amount in milliliters"
          />
          <button
            style={styles.customButton}
            onClick={() => {
              const input = document.getElementById('customWaterInput');
              const amount = parseFloat(input.value);
              if (amount > 0 && amount <= 5000) {
                handleAddWater(amount);
                input.value = '';
              } else {
                showError('אנא הזן כמות בין 1 ל-5000 מ"ל');
              }
            }}
            disabled={loading}
            className="btn-primary"
            aria-label="Add custom amount of water"
            type="button">
            הוסף
          </button>
        </div>
      </div>

      {/* Info Card */}
      <div style={styles.infoCard}>
        <div style={styles.infoTitle}><span aria-hidden="true">💡</span> טיפים</div>
        <div style={styles.infoText}>
          • שתה כוס מים בבוקר<br/>
          • שמור בקבוק מים לידך<br/>
          • שתה לפני ואחרי אימון<br/>
          • הוסף לימון למים לטעם
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    flex: 1,
    backgroundColor: colors.bg,
    minHeight: '100vh',
    paddingBottom: 20,
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '20px',
    paddingTop: '24px',
  },
  backButton: {
    background: 'none',
    border: 'none',
    color: colors.primary,
    fontSize: 24,
    cursor: 'pointer',
    padding: 8,
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: colors.textPrimary,
    flex: 1,
    textAlign: 'center',
  },
  headerRight: {
    width: 40,
  },
  dateSection: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 20px',
    marginBottom: 24,
    gap: 16,
  },
  dateNavButton: {
    background: 'none',
    border: 'none',
    color: colors.primary,
    fontSize: 24,
    cursor: 'pointer',
    padding: 8,
    opacity: 1,
  },
  dateText: {
    color: colors.textPrimary,
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'center',
    minWidth: 200,
  },
  intakeCard: {
    backgroundColor: colors.surface,
    borderRadius: 20,
    padding: 40,
    margin: '0 20px 24px',
    border: `1px solid ${colors.border}`,
    textAlign: 'center',
    background: `linear-gradient(135deg, ${colors.surface} 0%, ${colors.surfaceAlt} 100%)`,
  },
  intakeValue: {
    fontSize: 64,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 8,
  },
  intakeUnit: {
    fontSize: 24,
    color: colors.textTertiary,
    marginBottom: 8,
  },
  intakeGoal: {
    fontSize: 16,
    color: colors.textSecondary,
  },
  progressSection: {
    padding: '0 20px',
    marginBottom: 24,
  },
  progressBarContainer: {
    width: '100%',
    height: 12,
    backgroundColor: colors.surface,
    borderRadius: 6,
    overflow: 'hidden',
    border: `1px solid ${colors.border}`,
    marginBottom: 8,
  },
  progressBar: {
    height: '100%',
    backgroundColor: colors.primary,
    borderRadius: 6,
    transition: 'width 300ms ease',
  },
  progressText: {
    fontSize: 14,
    color: colors.textTertiary,
    textAlign: 'center',
  },
  quickAddSection: {
    padding: '0 20px',
    marginBottom: 24,
  },
  quickAddTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.textPrimary,
    marginBottom: 12,
    textAlign: 'right',
  },
  quickAddButtons: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 12,
  },
  quickAddButton: {
    height: 56,
    backgroundColor: colors.surface,
    color: colors.primary,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    fontSize: 16,
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 200ms ease',
  },
  remainingCard: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 20,
    margin: '0 20px 24px',
    border: `1px solid ${colors.border}`,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  remainingIcon: {
    fontSize: 32,
  },
  remainingText: {
    fontSize: 16,
    color: colors.textPrimary,
    flex: 1,
    textAlign: 'right',
  },
  successCard: {
    backgroundColor: colors.surfaceAlt,
    borderRadius: 12,
    padding: 20,
    margin: '0 20px 24px',
    border: `1px solid ${colors.primary}`,
    textAlign: 'center',
  },
  successIcon: {
    fontSize: 48,
    marginBottom: 8,
  },
  successText: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.primary,
  },
  customSection: {
    padding: '0 20px',
    marginBottom: 24,
  },
  customTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.textPrimary,
    marginBottom: 12,
    textAlign: 'right',
  },
  customInputContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
  },
  customInput: {
    flex: 1,
    height: 48,
    backgroundColor: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 12,
    padding: '12px 16px',
    fontSize: 16,
    color: colors.textPrimary,
    outline: 'none',
    textAlign: 'right',
  },
  customButton: {
    height: 48,
    padding: '12px 24px',
    backgroundColor: colors.primary,
    color: colors.textOnPrimary,
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: '600',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  infoCard: {
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: 20,
    margin: '0 20px',
    border: `1px solid ${colors.border}`,
  },
  infoTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: colors.textPrimary,
    marginBottom: 12,
    textAlign: 'right',
  },
  infoText: {
    fontSize: 14,
    color: colors.textTertiary,
    lineHeight: 1.8,
    textAlign: 'right',
  },
};

export default WaterTrackerScreen;

